/**
 * OrganizationSettings.tsx
 * 
 * 組織設定ページ
 * Issue #268 - マルチテナント組織管理機能の実装
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,

  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  CreditCard as BillingIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

// Components
import MemberManagement, { type MemberInfo } from '../components/organization/MemberManagement';
import { useOrganization } from '../hooks/useOrganization';
import { useAuthContext } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';

// Types

/**
 * タブの値
 */
type TabValue = 'general' | 'members' | 'billing' | 'security';

/**
 * 組織の基本情報
 */
interface OrganizationInfo {
  name: string;
  description: string;
  businessType: string;
  address: string;
  phoneNumber: string;
  email: string;
  website?: string;
  industry: string;
  size: string;
}

/**
 * 請求設定
 */
interface BillingSettings {
  planType: 'light' | 'standard' | 'premium';
  autoRenewal: boolean;
  billingEmail: string;
  paymentMethod: string;
  nextBillingDate?: Date;
  currentUsage: {
    users: number;
    storage: number;
    apiCalls: number;
  };
}

/**
 * セキュリティ設定
 */
interface SecuritySettings {
  requireMFA: boolean;
  enforceSSO: boolean;
  sessionTimeout: number;
  allowedDomains: string[];
  ipWhitelist: string[];
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
  };
}

/**
 * OrganizationSettingsコンポーネント
 */
const OrganizationSettings: React.FC = () => {
  usePageTitle('組織設定');
  // ローカルステート
  const [currentTab, setCurrentTab] = useState<TabValue>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 組織情報
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo>({
    name: '',
    description: '',
    businessType: '',
    address: '',
    phoneNumber: '',
    email: '',
    website: '',
    industry: '',
    size: '',
  });

  // 請求設定
  const [billingSettings, setBillingSettings] = useState<BillingSettings>({
    planType: 'light',
    autoRenewal: true,
    billingEmail: '',
    paymentMethod: '',
    currentUsage: {
      users: 0,
      storage: 0,
      apiCalls: 0,
    },
  });

  // セキュリティ設定
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    requireMFA: false,
    enforceSSO: false,
    sessionTimeout: 480,
    allowedDomains: [],
    ipWhitelist: [],
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: false,
    },
  });

  // 通知
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  // Hooks
  const { currentTenant } = useOrganization();
  const { user } = useAuthContext();

  // 権限チェック
  const canEditSettings = user?.tenantRole === 'owner' || user?.tenantRole === 'admin';
  const canManageMembers = user?.tenantRole === 'owner' || user?.tenantRole === 'admin';
  const canAccessBilling = user?.tenantRole === 'owner';
  const canAccessSecurity = user?.tenantRole === 'owner';

  /**
   * 組織設定を読み込み
   * 🔥 FIX: Remove unnecessary dependencies to prevent infinite loops
   */
  const loadOrganizationSettings = useCallback(async () => {
    if (!currentTenant) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 🔥 FIX: Get user email at runtime instead of dependency
      const currentUserEmail = user?.email || '';

      // 組織基本情報の設定
      setOrganizationInfo({
        name: currentTenant.name,
        description: currentTenant.settings?.customFields?.find(f => f.name === 'description')?.type || '',
        businessType: 'beauty_salon',
        address: '東京都渋谷区',
        phoneNumber: '03-1234-5678',
        email: currentUserEmail,
        website: '',
        industry: 'beauty_wellness',
        size: 'small',
      });

      // 請求設定の設定
      setBillingSettings({
        planType: currentTenant.planType,
        autoRenewal: true,
        billingEmail: currentUserEmail,
        paymentMethod: 'credit_card',
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        currentUsage: {
          users: currentTenant.limits?.currentUsers || 1,
          storage: 50, // MB
          apiCalls: 1000,
        },
      });

      // セキュリティ設定の設定
      setSecuritySettings({
        requireMFA: currentTenant.settings?.requireMFA || false,
        enforceSSO: currentTenant.settings?.enforceSSO || false,
        sessionTimeout: currentTenant.settings?.sessionTimeout || 480,
        allowedDomains: [],
        ipWhitelist: [],
        passwordPolicy: currentTenant.settings?.passwordPolicy || {
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: false,
        },
      });

    } catch (err: any) {
      console.error('Failed to load organization settings:', err);
      const errorMessage = err.message || '組織設定の読み込み中にエラーが発生しました';
      setError(errorMessage);

      // 🔥 FIX: Show user-friendly error notification
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [currentTenant]); // 🔥 FIX: Only depend on currentTenant, get user email at runtime

  /**
   * 組織設定を保存
   */
  const saveOrganizationSettings = useCallback(async () => {
    if (!currentTenant || !canEditSettings) return;

    try {
      setSaving(true);
      setError(null);

      // 実際のAPIコールはここで実装
      // 開発環境用のモック処理
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSnackbar({
        open: true,
        message: '設定が正常に保存されました',
        severity: 'success',
      });

    } catch (err: any) {
      console.error('Failed to save organization settings:', err);
      setSnackbar({
        open: true,
        message: err.message || '設定の保存中にエラーが発生しました',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [currentTenant, canEditSettings]);

  /**
   * 初期化
   * 🔥 FIX: Stabilize useEffect by only depending on tenant ID to prevent loops
   */
  useEffect(() => {
    if (currentTenant) {
      loadOrganizationSettings();
    } else {
      setLoading(false);
    }
  }, [currentTenant?.id, loadOrganizationSettings]); // 🔥 FIX: Depend on stable tenant ID

  /**
   * タブコンテンツをレンダリング
   */
  const renderTabContent = () => {
    switch (currentTab) {
      case 'general':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardHeader
                  title="基本情報"
                  subheader="組織の基本的な情報を管理します"
                  action={
                    <Tooltip title={canEditSettings ? '編集可能' : '編集権限がありません'}>
                      <EditIcon color={canEditSettings ? 'primary' : 'disabled'} />
                    </Tooltip>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="組織名"
                        value={organizationInfo.name}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, name: e.target.value }))}
                        disabled={!canEditSettings}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="説明"
                        value={organizationInfo.description}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, description: e.target.value }))}
                        disabled={!canEditSettings}
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="業種"
                        value={organizationInfo.businessType}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, businessType: e.target.value }))}
                        disabled={!canEditSettings}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="企業規模"
                        value={organizationInfo.size}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, size: e.target.value }))}
                        disabled={!canEditSettings}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="住所"
                        value={organizationInfo.address}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, address: e.target.value }))}
                        disabled={!canEditSettings}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="電話番号"
                        value={organizationInfo.phoneNumber}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        disabled={!canEditSettings}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="メールアドレス"
                        value={organizationInfo.email}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, email: e.target.value }))}
                        disabled={!canEditSettings}
                        type="email"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="ウェブサイト"
                        value={organizationInfo.website}
                        onChange={(e) => setOrganizationInfo(prev => ({ ...prev, website: e.target.value }))}
                        disabled={!canEditSettings}
                        type="url"
                      />
                    </Grid>
                  </Grid>

                  {canEditSettings && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveOrganizationSettings}
                        disabled={saving}
                      >
                        {saving ? <CircularProgress size={20} /> : '保存'}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="組織統計" />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      プランタイプ
                    </Typography>
                    <Typography variant="h6">
                      {billingSettings.planType.toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      ユーザー数
                    </Typography>
                    <Typography variant="h6">
                      {billingSettings.currentUsage.users} / {currentTenant?.limits?.maxUsers || 50}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      ストレージ使用量
                    </Typography>
                    <Typography variant="h6">
                      {billingSettings.currentUsage.storage} MB
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'members':
        if (!canManageMembers) {
          return (
            <Alert severity="warning">
              メンバー管理にアクセスする権限がありません。
            </Alert>
          );
        }
        return (
          <MemberManagement
            organizationId={currentTenant?.id || ''}
            currentUserRole={user?.tenantRole as 'owner' | 'admin' | 'manager' | 'staff' | 'viewer' || 'viewer'}
            onMemberUpdated={(member: MemberInfo) => {
              setSnackbar({
                open: true,
                message: `${member.firstName && member.lastName ? 
                  `${member.lastName} ${member.firstName}` : 
                  member.email}の設定が更新されました`,
                severity: 'success',
              });
            }}
            onMemberRemoved={(_memberId: string) => {
              setSnackbar({
                open: true,
                message: 'メンバーが削除されました',
                severity: 'success',
              });
            }}
          />
        );

      case 'billing':
        if (!canAccessBilling) {
          return (
            <Alert severity="warning">
              請求設定にアクセスする権限がありません。
            </Alert>
          );
        }
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardHeader title="プラン・請求設定" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        現在のプラン: {billingSettings.planType.toUpperCase()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        次回請求日: {billingSettings.nextBillingDate?.toLocaleDateString('ja-JP')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={billingSettings.autoRenewal}
                            onChange={(e) => setBillingSettings(prev => ({ ...prev, autoRenewal: e.target.checked }))}
                          />
                        }
                        label="自動更新"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="請求先メールアドレス"
                        value={billingSettings.billingEmail}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, billingEmail: e.target.value }))}
                        type="email"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveOrganizationSettings}
                      disabled={saving}
                    >
                      保存
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="使用状況" />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      ユーザー数
                    </Typography>
                    <Typography variant="h6">
                      {billingSettings.currentUsage.users} / {currentTenant?.limits?.maxUsers}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      API使用量（今月）
                    </Typography>
                    <Typography variant="h6">
                      {billingSettings.currentUsage.apiCalls.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'security':
        if (!canAccessSecurity) {
          return (
            <Alert severity="warning">
              セキュリティ設定にアクセスする権限がありません。
            </Alert>
          );
        }
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="セキュリティ設定" />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        認証設定
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={securitySettings.requireMFA}
                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, requireMFA: e.target.checked }))}
                          />
                        }
                        label="多要素認証を必須にする"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={securitySettings.enforceSSO}
                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, enforceSSO: e.target.checked }))}
                          />
                        }
                        label="SSO認証を強制する"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="セッションタイムアウト（分）"
                        type="number"
                        value={securitySettings.sessionTimeout}
                        onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                        inputProps={{ min: 1, max: 1440 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        パスワードポリシー
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="最小文字数"
                            type="number"
                            value={securitySettings.passwordPolicy.minLength}
                            onChange={(e) => setSecuritySettings(prev => ({
                              ...prev,
                              passwordPolicy: { ...prev.passwordPolicy, minLength: parseInt(e.target.value) }
                            }))}
                            inputProps={{ min: 6, max: 128 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={securitySettings.passwordPolicy.requireUppercase}
                                onChange={(e) => setSecuritySettings(prev => ({
                                  ...prev,
                                  passwordPolicy: { ...prev.passwordPolicy, requireUppercase: e.target.checked }
                                }))}
                              />
                            }
                            label="大文字を必須にする"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={securitySettings.passwordPolicy.requireLowercase}
                                onChange={(e) => setSecuritySettings(prev => ({
                                  ...prev,
                                  passwordPolicy: { ...prev.passwordPolicy, requireLowercase: e.target.checked }
                                }))}
                              />
                            }
                            label="小文字を必須にする"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={securitySettings.passwordPolicy.requireNumbers}
                                onChange={(e) => setSecuritySettings(prev => ({
                                  ...prev,
                                  passwordPolicy: { ...prev.passwordPolicy, requireNumbers: e.target.checked }
                                }))}
                              />
                            }
                            label="数字を必須にする"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={securitySettings.passwordPolicy.requireSpecialChars}
                                onChange={(e) => setSecuritySettings(prev => ({
                                  ...prev,
                                  passwordPolicy: { ...prev.passwordPolicy, requireSpecialChars: e.target.checked }
                                }))}
                              />
                            }
                            label="特殊文字を必須にする"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveOrganizationSettings}
                      disabled={saving}
                    >
                      保存
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // 🔥 FIX: Improved loading state management
  if (!currentTenant) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          組織情報が見つかりません。ログインし直してください。
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            組織設定
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentTenant.name}の設定を読み込み中...
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ページヘッダー */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          組織設定
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {currentTenant.name}の設定を管理します
        </Typography>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* タブ */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="基本情報"
            value="general"
            icon={<BusinessIcon />}
            iconPosition="start"
          />
          <Tab
            label="メンバー"
            value="members"
            icon={<PeopleIcon />}
            iconPosition="start"
            disabled={!canManageMembers}
          />
          <Tab
            label="請求"
            value="billing"
            icon={<BillingIcon />}
            iconPosition="start"
            disabled={!canAccessBilling}
          />
          <Tab
            label="セキュリティ"
            value="security"
            icon={<SecurityIcon />}
            iconPosition="start"
            disabled={!canAccessSecurity}
          />
        </Tabs>
      </Paper>

      {/* タブコンテンツ */}
      <Box>{renderTabContent()}</Box>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationSettings;